import React from "react";

import { usePutSuppressionFilterQuery } from "@tiny/api";
import { useRefreshRegister } from "@tiny/components/src/RefreshProvider";
import useSearchable from "@tiny/components/src/hooks/useSearchable";
import useSelect from "@tiny/components/src/hooks/useSelect";
import { pageableDefault } from "./SuppressionDetailsList.helpers";
import {
  CustomerStatuses,
  CustomerStatus,
  SuppressionDetailsListProps,
} from "./SuppressionDetailsList.types";
import { SuppressionItemInfoView } from "@tiny/api";
import { useApi } from "@tiny/api";
import {
  downloadFileData,
  ResponseHeaders,
} from "@tiny/components/src/utils/fileUtils";
import { useMessages } from "@tiny/components";
import {
  extractBackendError,
  getErrorMessage,
} from "@tiny/components/src/utils/errorUtils";
import { showUploadMessage } from "@tiny/components/src/Snackbar/snackbar-helpers";
import { closeSnackbar } from "notistack";

export function useSuppressionDetailsList(props: SuppressionDetailsListProps) {
  const api = useApi();
  const {
    suppressionId,
    defaultPageable = pageableDefault,
    onAfterDelete,
    pageName = "",
  } = props;

  const { showErrorMessage } = useMessages();

  const [search, setSearch] = React.useState("");

  const [deleteOpen, setDeleteOpen] = React.useState(false);

  const [filterOpen, setFilterOpen] = React.useState(false);

  const [filterStatuses, setFilterStatuses] = React.useState<CustomerStatuses>(
    []
  );

  const [filterTags, setFilterTags] = React.useState<string[]>([]);

  const [columnsSettingsVisible, setColumnsSettingsVisible] =
    React.useState(false);

  const [activeCustomer, setActiveCustomer] =
    React.useState<SuppressionItemInfoView>();

  const { pageable, handlePageChange, handleSizeChange, handleSortChange } =
    useSearchable(defaultPageable, pageName);

  const { selected, handleDeselectAll } = useSelect<SuppressionItemInfoView>({
    isEqual: (a: SuppressionItemInfoView, b: SuppressionItemInfoView) =>
      a.id === b.id,
  });

  const {
    data,
    refetch: refetchSuppressionDetails,
    isLoading,
  } = usePutSuppressionFilterQuery(
    {
      suppressionId,
      searchText: search,
    },
    pageable,
    {
      keepPreviousData: true,
    }
  );

  useRefreshRegister(async () => {
    await refetchSuppressionDetails();
  });

  const selectSubscriber = React.useCallback((id: string) => {
    return;
  }, []);

  const deselectAllSuppressionDetails = React.useCallback(() => {
    handleDeselectAll();
  }, [handleDeselectAll]);

  const deleteSelectedSuppressionDetails = React.useCallback(async () => {
    setActiveCustomer(undefined);
    setDeleteOpen(true);
  }, []);

  const exportSuppressionDetails = React.useCallback(
    async (type: string, columns: string[]) => {
      if (type === "CSV") {
        try {
          showUploadMessage({
            variant: "uploading",
            message: "Exporting",
            description: "Your suppression list is exporting",
            persist: true,
            showClose: true,
          });
          const { data, headers } = await api.putSuppressionFilterExport(
            {
              searchText: search,
              suppressionId,
            },
            {
              format: "blob",
              headers: {
                "Access-Control-Expose-Headers": "Content-Disposition",
              },
            }
          );
          const exportHeaders = headers || {};
          if (!exportHeaders?.["content-disposition"]) {
            exportHeaders["content-disposition"] = "fileName=suppression.csv";
          }
          downloadFileData(data, exportHeaders as ResponseHeaders);
          closeSnackbar();
          showUploadMessage({
            variant: "success",
            message: "Exported successfully!",
            description:
              "Your suppression list has been exported successfully!",
            primaryCtaActionText: "Close",
            primaryCtaActionFunction: () => {
              closeSnackbar();
            },
            persist: false,
          });
        } catch (e: any) {
          extractBackendError(e.response).then((be) => {
            showErrorMessage(getErrorMessage(be));
          });
        }
      }
    },
    [search, suppressionId, showErrorMessage]
  );

  const toggleAllSuppressionDetails = React.useCallback((checked: boolean) => {
    return;
  }, []);

  const sortSuppressionDetails = React.useCallback(
    (sort: string[]) => {
      handleSortChange(sort);
    },
    [handleSortChange]
  );

  const searchSuppressionDetails = React.useCallback(
    async (value: string) => {
      handlePageChange(0);
      setSearch(value);
    },
    [handlePageChange]
  );

  const openDelete = React.useCallback((customer: SuppressionItemInfoView) => {
    setActiveCustomer(customer);
    setDeleteOpen(true);
  }, []);

  const closeDelete = React.useCallback(() => {
    setActiveCustomer(undefined);
    setDeleteOpen(false);
  }, []);

  const openFilter = React.useCallback(() => {
    setFilterOpen(true);
  }, []);

  const closeFilter = React.useCallback(() => {
    setFilterOpen(false);
  }, []);

  const successDelete = React.useCallback(() => {
    if (activeCustomer) {
      setActiveCustomer(undefined);
    } else {
      handleDeselectAll();
    }
    refetchSuppressionDetails();
    onAfterDelete?.();
    setDeleteOpen(false);
  }, [
    activeCustomer,
    handleDeselectAll,
    onAfterDelete,
    refetchSuppressionDetails,
  ]);

  const successEdit = React.useCallback(() => {
    refetchSuppressionDetails();
  }, [refetchSuppressionDetails]);

  const openColumnsSettings = React.useCallback(() => {
    setColumnsSettingsVisible(true);
  }, []);

  const closeColumnsSettings = React.useCallback(() => {
    setColumnsSettingsVisible(false);
  }, []);

  const saveFilters = React.useCallback(
    (statuses: CustomerStatuses, tags: string[]) => {
      setFilterStatuses(statuses);
      setFilterTags(tags);
      closeFilter();
    },
    [closeFilter]
  );

  const deselectTag = React.useCallback((tag: string) => {
    setFilterTags((prev) => prev.filter((item) => item !== tag));
  }, []);

  const deselectStatus = React.useCallback((status: CustomerStatus) => {
    setFilterStatuses((prev) => prev.filter((item) => item !== status));
  }, []);

  const allSuppressionDetailsSelected = React.useMemo(() => {
    return false;
  }, []);

  const selectedSuppressionDetailsIds = React.useMemo(() => {
    return selected.map((item) => item.id!);
  }, [selected]);

  const sort = pageable.sort;
  const currentPage = pageable.page;
  const pageSize = pageable.size;
  const isSearching = !!search;
  const suppressions = data?.content || [];
  const totalElements = data?.totalElements || 0;

  return {
    activeCustomer,
    allSuppressionDetailsSelected,
    closeDelete,
    currentPage,
    deleteOpen,
    deleteSelectedSuppressionDetails,
    deselectAllSuppressionDetails,
    exportSuppressionDetails,
    handlePageChange,
    handleSizeChange,
    isLoading,
    isSearching,
    pageSize,
    searchSuppressionDetails,
    selectSubscriber,
    selected,
    selectedSuppressionDetailsIds,
    sort,
    sortSuppressionDetails,
    suppressions,
    successDelete,
    successEdit,
    toggleAllSuppressionDetails,
    totalElements,
    openDelete,
    columnsSettingsVisible,
    openColumnsSettings,
    closeColumnsSettings,
    filterOpen,
    openFilter,
    closeFilter,
    saveFilters,
    filterStatuses,
    filterTags,
    deselectTag,
    deselectStatus,
  };
}
